import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ITransformScript } from "../../../../models/TransformScript";
import { ServiceManager } from "../../../../services/ServiceManager";
import Layout from "../../../layout";
import MISButton from "../../../../components/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faArrowLeft, faGripVertical, faSave, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Col, Container, Row, Stack } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { useForm, useFieldArray } from "react-hook-form";
import { useEffect, useRef } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { APIStatusCode, Permissions } from "../../../../app/enums";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePermissionCheck } from "../../../../app/common/helper/Permissions";
import MISTooltip from "../../../../components/tooltip";
import { PERMISSION_REQUIRED, UNSAVED_CHANGE_MESSAGE } from "../../../../app/constants";

function SortableItem({ id, field, index, register, errors, handleUnsavedChanges, canEditEstablishments, handleRemove }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: !canEditEstablishments });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Row className="transform-field-row mb-5">
        <Col md={1} className="me-2" style={{ flex: '0' }}>
          <span className="transform-number-circle">{index + 1}</span>
        </Col>
        <Col md={1} style={{ flex: '0' }}>
          <FontAwesomeIcon 
            icon={faGripVertical} 
            className="gray-color me-3" 
            {...listeners}
            style={{ cursor: canEditEstablishments ? 'grab' : 'default' }}
          />
        </Col>
        <Col md={7} className="transform-field-text p-0 me-3 position-relative">
          <label className="transform-field-name">Script</label>
          <input 
            {...register(`data[${index}].scriptText`, { 
              required: "Transform Script is required", 
              validate: { 
                duplicate: (value: any, allValues: any) => {
                  const scriptTextValues = allValues.data.map((item: any) => item.scriptText);
                  const isDuplicate = scriptTextValues.filter((text: any) => text === value).length > 1;
                  return !isDuplicate || 'Duplicate Script is not allowed';
                }
              } 
            })}
            defaultValue={field.scriptText || ''} 
            className={`form-control mt-1 ms-0 w-100 ${errors.data?.[index]?.scriptText ? 'is-invalid' : ''}`}
            onChange={handleUnsavedChanges} 
            disabled={!canEditEstablishments}
          />
          <ErrorMessage
            errors={errors}
            name={`data[${index}].scriptText`}
            render={({ message }: any) => <div className="invalid-feedback position-absolute" style={{ display: 'block', color: '#DC3245' }}>{message}</div>}
          />
        </Col>
        <Col md={3}>
          {canEditEstablishments ? (
            <button className="btn btn-light red-color" onClick={() => handleRemove(index)}>
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          ) : (
            <MISTooltip message={PERMISSION_REQUIRED} placement="bottom">
              <div className="disabled" style={{width: '15%'}}>
                <button className="btn btn-light red-color" disabled>
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>
            </MISTooltip>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default function TransformScript() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { establishmentId, clientId } = useParams();
  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const canEditEstablishments = usePermissionCheck(Permissions.CanEditEstablishments);

  const { isLoading, data: transformData } = useQuery<ITransformScript[]>({
    queryKey: [`transform-script-${clientId}`],
    queryFn: () => ServiceManager.EstablishmentService.GetTransformScripts(establishmentId, clientId),
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: (model: ITransformScript) => {
      return ServiceManager.EstablishmentService.UpdateTransformScripts(establishmentId, clientId, model);
    },
    onError: (error, variables, context) => {
      // Error handling
    },
    onSuccess: (data, variables, context) => {
      if (data === null) {
        ServiceManager.ToastService.showError("Unable to save transforms.");
      }
      else {
        if (data.status === APIStatusCode.Ok) {
          ServiceManager.ToastService.showSuccess("Transform Scripts saved successfully.");
          queryClient.invalidateQueries({ queryKey: [`transform-script-${clientId}`] });
        }
        else {
          let errorMessage = (data.response && data.response.data) ? data.response.data : data.message;
          ServiceManager.ToastService.showError(errorMessage.toString());
        }
      }
    }
  });

  const { register, handleSubmit, reset, getValues, control, formState: { errors } } = useForm();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'data',
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!canEditEstablishments) return;
    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((field: any) => field.id === active.id);
      const newIndex = fields.findIndex((field: any) => field.id === over?.id);
      move(oldIndex, newIndex);
    }
  };

  const handleRemove = (index: any) => {
    remove(index);
    reset({ data: getValues().data });
  };

  const handleUnsavedChanges = (event: any) => {
    event.preventDefault();
  };

  const isUnsavedChanges = (transformData: any) => {
    const prevFormData = getValues().data;

    const filteredFormData = prevFormData.filter((obj: any) => {
      const scriptTextValue = obj.scriptText;
      return scriptTextValue !== '' && scriptTextValue !== undefined;
    });

    if ((filteredFormData && filteredFormData.length === 0) && (transformData && transformData.length === 0)) {
      return false;
    }

    const sortedFormData = JSON.stringify(filteredFormData.sort());
    const sortedTransformData = JSON.stringify(transformData.sort());

    return sortedFormData !== sortedTransformData;
  };

  const handleUnsavedChangesClick = () => {
    if (isUnsavedChanges(transformData)) {
      const shouldLeave = window.confirm(UNSAVED_CHANGE_MESSAGE);
      if (shouldLeave) {
        navigate(-1);
      }
    }
    else {
      navigate(-1);
    }
  };

  const handleBeforeUnload = (event: any) => {
    if (isUnsavedChanges(transformData)) {
      const message = UNSAVED_CHANGE_MESSAGE;
      event.returnValue = message;
      return message;
    }
  };

  useEffect(() => {
    if (!isLoading && transformData) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
    // eslint-disable-next-line
  }, [isLoading, transformData]);

  useEffect(() => {
    if (!isLoading && transformData) {
      reset({ data: [] });

      if (transformData.length > 0) {
        transformData.forEach((item: any, index: any) => {
          append(item);
        });
      }
      else {
        append({});
      }
    }
  }, [isLoading, transformData, append, reset]);

  const onSubmit = (data: any) => {
    const updatedTransformScript = data.data.map((fieldMapping: ITransformScript, index: number) => (
      {
        ...fieldMapping,
        priority: index + 1,
      }
    ));
    mutation.mutate(updatedTransformScript);
  };

  return (
    <Layout 
      title="Transform"
      button={
        canEditEstablishments ?
          <MISButton 
            text="Save" 
            className="btn btn-primary crbc-bg-color me-2" 
            type="submit" 
            form="hook-form" 
            disabled={mutation.isPending}
            FnOnClick={() => refSubmitButtom?.current?.click()} 
            icon={<FontAwesomeIcon icon={faSave} />}
          />
          :
          <MISTooltip message={PERMISSION_REQUIRED} placement="bottom">
            <div className="disabled">
              <MISButton 
                text="Save" 
                className="btn btn-secondary me-2" 
                FnOnClick={() => null} 
                disabled 
                icon={<FontAwesomeIcon icon={faSave} />}
              />
            </div>
          </MISTooltip>
      }
      backArrow={
        <FontAwesomeIcon 
          icon={faArrowLeft} 
          onClick={handleUnsavedChangesClick} 
          className="me-4 fs-5 cursor-pointer" 
        />
      }
    >
      {isLoading ? (
        <div className="mis-loader">
          <Row className="pb-3">
            <Col md={3}><Skeleton height={20} /></Col>
            <Col md={6}><Skeleton height={20} /></Col>
            <Col md={3}><Skeleton height={20} /></Col>
          </Row>
          <Row className="pb-3">
            <Col md={3}><Skeleton height={20} /></Col>
            <Col md={6}><Skeleton height={20} /></Col>
            <Col md={3}><Skeleton height={20} /></Col>
          </Row>
          <Row className="pb-3">
            <Col md={3}><Skeleton height={20} /></Col>
            <Col md={6}><Skeleton height={20} /></Col>
            <Col md={3}><Skeleton height={20} /></Col>
          </Row>
        </div>
      ) : (
        <>
          <Stack direction="horizontal">
            {canEditEstablishments ? (
              <MISButton 
                text="New" 
                className="btn btn-secondary btn-small me-2" 
                FnOnClick={() => append({ scriptText: '' })} 
                icon={<FontAwesomeIcon icon={faAdd} />} 
                disabled={fields.length >= 15}
              />
            ) : (
              <MISTooltip message={PERMISSION_REQUIRED} placement="bottom">
                <div className="disabled">
                  <MISButton 
                    text="New" 
                    className="btn btn-secondary btn-small me-2" 
                    FnOnClick={() => null} 
                    disabled 
                    icon={<FontAwesomeIcon icon={faAdd} />}
                  />
                </div>
              </MISTooltip>
            )}
          </Stack>

          <Stack className="pt-3 overflow-auto" style={{ height: '80vh' }}>
            <form onSubmit={handleSubmit(onSubmit)} className="form">
              <Container className="mt-3">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={fields.map((field: any) => field.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {fields.map((field: any, index: number) => (
                      <SortableItem
                        key={field.id}
                        id={field.id}
                        field={field}
                        index={index}
                        register={register}
                        errors={errors}
                        handleUnsavedChanges={handleUnsavedChanges}
                        canEditEstablishments={canEditEstablishments}
                        handleRemove={handleRemove}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Container>
              <button hidden={true} ref={refSubmitButtom} type={"submit"} />
            </form>
          </Stack>
        </>
      )}
    </Layout>
  );
}